/*
 * Asset Library launcher — the task pane's half of the Office Dialog
 * conversation, so DeckorateModule only has to call `open()`.
 *
 * The Asset Library lives in the dialog WINDOW for the whole session (its
 * own loading/error/Retry/insert-result UI). The pane only does what a
 * dialog window physically cannot: fetch the slide-template manifest and org
 * brand templates (no AuthStateProvider in a dialog, so no bearer token),
 * insert via PowerPoint.run() (never available in a dialog), and close the
 * window.
 *
 * The in-pane overlay is used ONLY where there's no Office Dialog API at all
 * (browser dev, tests).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { logEvent } from "../../../../../telemetry";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { HttpStatusError, type HttpClient } from "../../../services/deckorateApi";
import {
  fetchSlideTemplateManifest,
  fetchSlideTemplatesFromSasInfo,
  requestSlideTemplatesSasInfo,
  logSlideTemplatesError,
  type SlideTemplate,
  type SlideTemplatesSasInfo,
} from "../../slide-templates/services/slideTemplatesApi";
import {
  DeckReadOnlyError,
  SelectionUnavailableError,
  classifyInsertFailure,
  insertSlideTemplates,
  isDeckReadOnly,
  slideTemplatesAddedMessage,
  type InsertFailureKind,
} from "../../slide-templates/services/slideTemplateInsert";
import { getErrorMessage } from "../../PhotoHeadshot/services/photoHeadApi";
import {
  BlobFetchHttpError,
  brandLayoutsAddedMessage,
  fetchSavedBrandTemplates,
  insertBrandTemplateLayouts,
  logSavedBrandTemplatesError,
  type SavedBrandTemplate,
} from "../../apply-brand/services/brandTemplatesApi";
import {
  notifyPreviewedTemplateUnavailable, setPendingSavedTemplateSelection,
} from "../../apply-brand/services/pendingSavedTemplateSelection";
import { requestDeckorateView } from "../../../../../../integrations/tools/deckorate";
import {
  fetchPresentationTemplatesFromSasInfo,
  requestDirectorySasInfo,
  logPresentationTemplatesError,
  type DirectorySasInfo,
  type PresentationTemplate,
} from "../../presentation-templates/services/presentationTemplatesApi";
import {
  PresentationDeckReadOnlyError,
  PresentationSelectionUnavailableError,
  classifyInsertFailure as classifyPresentationInsertFailure,
  insertPresentationTemplates,
  isPresentationDeckReadOnly,
  presentationSlidesAddedMessage,
  sourcePresentationNames,
} from "../../presentation-templates/services/presentationTemplateInsert";
import {
  FALLBACK_BRAND_INSERT_ERROR,
  FALLBACK_INSERT_ERROR,
  FALLBACK_SAS_ERROR,
  FALLBACK_BRAND_TEMPLATES_ERROR,
  FALLBACK_PRESENTATION_SAS_ERROR,
  FALLBACK_PRESENTATION_INSERT_ERROR,
  type DialogToPaneMessage,
} from "../services/assetLibraryDialogChannel";
import { clearBrandTemplatesHandoff, writeBrandTemplatesHandoff } from "../services/brandTemplatesHandoff";
import { clearPendingBrandTemplatePreview } from "../services/brandTemplatePreviewHandoff";
import { canUseDialog, openAssetLibraryDialog, type AssetLibrarySession } from "./openAssetLibraryDialog";
import { isAssetLibraryTabId, type AssetLibraryTabId } from "../types";

const DECKORATE_CONNECTOR = "deckorate";

const BRAND_TEMPLATES_HANDOFF_FAILED_MESSAGE = "Couldn't prepare the brand template list. Please try again.";
/**
 * Shown in the DIALOG's own error+Retry state (not a pane toast) on a 401 that
 * survived `fetchWithRefresh`'s own silent-refresh retry. Most commonly hit
 * right after a task-pane reload — the window opens and asks for the manifest
 * before AuthStateProvider has finished re-acquiring a token, so the very
 * first request loses that race even though sign-in itself is fine. Retry
 * (same button as any other manifest error) replays the request once the
 * token has caught up, so this must never close the window — the earlier
 * behaviour (close + a pane toast telling the user to sign in again) treated
 * that race as if the session had actually expired.
 */
const SESSION_STILL_STARTING_MESSAGE = "Couldn't load the slide template list yet — your session may still be starting. Try again in a moment.";

function isAuthFailure(error: unknown): boolean {
  return error instanceof HttpStatusError && error.status === 401;
}

/** The tab an insert failure notice belongs to. */
export type PaneNoticeSource = "slide-templates" | "brand-templates" | "presentation-templates";

interface PaneNoticeState {
  readonly kind: InsertFailureKind;
  readonly source: PaneNoticeSource;
}

export interface AssetLibraryLauncher {
  /**
   * Opens the Asset Library dialog window, optionally straight onto a tab
   * (the "Open Asset Library" button on a pane notice does this). Anything
   * that isn't a known tab id — e.g. a click event passed straight through as
   * a handler — is ignored and the library lands on its default tab.
   */
  readonly open: (initialTab?: unknown) => void;
  /** The tab the in-pane overlay should open on, when it is the one in use. */
  readonly inlineTab: AssetLibraryTabId | undefined;
  /** A failed/invalidated insert, waiting in the pane until dismissed. */
  readonly paneNotice: InsertFailureKind | null;
  /** Which tab the notice is about, since the "removed" wording names it. */
  readonly paneNoticeSource: PaneNoticeSource | null;
  readonly dismissPaneNotice: () => void;
  /**
   * True only where there's no Office Dialog API at all (browser dev, tests),
   * so the caller renders the in-pane overlay instead. Never true in a real
   * Office host.
   */
  readonly isInlineOpen: boolean;
  readonly closeInline: () => void;
}

/**
 * Serves one "ready"/"refresh": mints a directory SAS token and hands it
 * straight to the window (see assetLibraryDialogChannel.ts's module header).
 * Never throws — the window's error state IS the error report. The pane also
 * fetches its own copy in the background off the same sasInfo for serveInsert
 * (only the pane can call PowerPoint.run()); an insert that races ahead of it
 * just surfaces as insertSlideTemplates' ordinary "no insertable content".
 *
 * Manifest ONLY, not the combined fetch: this cache exists purely to resolve
 * an insert's `sourceFileUrl`/`slideIndex` (see serveInsert/insertSlideTemplates,
 * neither of which ever reads `thumbnailUrl`), so there is no reason to also
 * pay for the `.cache` sibling's real-photo download here — the window fetches
 * that itself, for display, on its own separate (non-blocking) schedule.
 */
async function serveManifest(
  http: HttpClient,
  session: AssetLibrarySession,
  cache: { current: readonly SlideTemplate[] },
  sasInfoRef: { current: SlideTemplatesSasInfo | null },
): Promise<void> {
  let sasInfo: SlideTemplatesSasInfo;
  try {
    sasInfo = await requestSlideTemplatesSasInfo(http);
  } catch (error) {
    const message = isAuthFailure(error) ? SESSION_STILL_STARTING_MESSAGE : getErrorMessage(error, FALLBACK_SAS_ERROR);
    logSlideTemplatesError("slide-templates-dialog-sas", message);
    // Stay open — the dialog has its own error state + Retry, a better
    // recovery path than closing and leaving only a pane toast.
    session.send({ type: "sas-error", message });
    return;
  }

  sasInfoRef.current = sasInfo;
  fetchSlideTemplateManifest(sasInfo)
    .then((templates) => {
      cache.current = templates;
    })
    .catch((error: unknown) => {
      logSlideTemplatesError("slide-templates-dialog-pane-cache", getErrorMessage(error, FALLBACK_SAS_ERROR));
    });

  session.send({ type: "sas-ready", sasUrl: sasInfo.directorySasUrl, manifestBlobPath: sasInfo.manifestBlobPath });
}

/** What the launcher does with the outcome of an insert the window asked for. */
interface InsertOutcomeHandlers {
  /** Success: confirm in the pane (the window closes). */
  readonly onAdded: (message: string) => void;
  /** A failure with its own pane notice (the window closes). */
  readonly onNotice: (kind: InsertFailureKind) => void;
}

/** The manifest an insert checks its selection against. */
interface CheckedManifest {
  readonly templates: readonly SlideTemplate[];
  /** False when nothing could be loaded to check against, so absence proves nothing. */
  readonly verified: boolean;
}

/**
 * The manifest as it stands right now. The copy fetched when the window
 * opened can be stale (an administrator may have removed a template since), so
 * an insert re-reads it; if that read fails the earlier copy is used, and only
 * an empty one — nothing to check against — rethrows.
 */
async function currentManifest(
  cache: { current: readonly SlideTemplate[] },
  sasInfoRef: { current: SlideTemplatesSasInfo | null },
): Promise<CheckedManifest> {
  if (!sasInfoRef.current) return { templates: cache.current, verified: cache.current.length > 0 };
  try {
    cache.current = await fetchSlideTemplatesFromSasInfo(sasInfoRef.current);
  } catch (error) {
    logSlideTemplatesError("slide-templates-dialog-manifest-recheck", getErrorMessage(error, FALLBACK_SAS_ERROR));
    if (cache.current.length === 0) throw error;
  }
  return { templates: cache.current, verified: true };
}

/**
 * Serves one "insert". Never throws, for the same reason as serveManifest.
 *
 * Success and the three BRD failures (read-only deck, lost connection, a
 * template an administrator removed) are all reported in the pane and close
 * the window, because there is nothing left for the user to do inside it. Any
 * other failure keeps the window open with its own inline message.
 */
async function serveInsert(
  session: AssetLibrarySession,
  cache: { current: readonly SlideTemplate[] },
  sasInfoRef: { current: SlideTemplatesSasInfo | null },
  ids: readonly string[],
  outcome: InsertOutcomeHandlers,
): Promise<void> {
  try {
    if (isDeckReadOnly()) throw new DeckReadOnlyError();
    const { templates, verified } = await currentManifest(cache, sasInfoRef);
    const available = new Set(templates.map((template) => template.id));
    if (verified && ids.some((id) => !available.has(id))) throw new SelectionUnavailableError();

    const inserted = await insertSlideTemplates(templates, ids);
    outcome.onAdded(slideTemplatesAddedMessage(inserted));
    session.close();
  } catch (error) {
    const kind = classifyInsertFailure(error);
    const message = getErrorMessage(error, FALLBACK_INSERT_ERROR);
    logSlideTemplatesError("slide-templates-dialog-insert", message);
    if (kind) {
      outcome.onNotice(kind);
      session.close();
      return;
    }
    session.send({ type: "insert-error", message });
  }
}

/** Serves one "brand-templates-request"/"brand-templates-refresh": fetch the
 *  org list, stage it, and tell the window. Never throws, same as
 *  serveManifest. My Templates (local uploads) never appears here. */
async function serveBrandTemplates(
  http: HttpClient,
  session: AssetLibrarySession,
  cache: { current: readonly SavedBrandTemplate[] },
): Promise<void> {
  let org: readonly SavedBrandTemplate[];
  try {
    org = await fetchSavedBrandTemplates(http);
  } catch (error) {
    const message = isAuthFailure(error) ? SESSION_STILL_STARTING_MESSAGE : getErrorMessage(error, FALLBACK_BRAND_TEMPLATES_ERROR);
    logSavedBrandTemplatesError("brand-templates-dialog-fetch", message);
    session.send({ type: "brand-templates-error", message });
    return;
  }

  cache.current = org;
  if (!writeBrandTemplatesHandoff(org)) {
    session.send({ type: "brand-templates-error", message: BRAND_TEMPLATES_HANDOFF_FAILED_MESSAGE });
    return;
  }
  session.send({ type: "brand-templates-ready" });
}

/** The current brand list: re-read on insert since the opening copy may be stale; falls back to it if the read fails. */
async function currentBrandTemplates(
  http: HttpClient,
  cache: { current: readonly SavedBrandTemplate[] },
): Promise<readonly SavedBrandTemplate[]> {
  try {
    cache.current = await fetchSavedBrandTemplates(http);
  } catch (error) {
    logSavedBrandTemplatesError("brand-templates-dialog-manifest-recheck", getErrorMessage(error, FALLBACK_BRAND_TEMPLATES_ERROR));
    if (cache.current.length === 0) throw error;
  }
  return cache.current;
}

/**
 * Serves "brand-templates-insert". Never throws: success and the BRD failures show a pane notice and close
 * the window; any other failure keeps it open with an inline message.
 */
async function serveBrandInsert(
  http: HttpClient,
  session: AssetLibrarySession,
  cache: { current: readonly SavedBrandTemplate[] },
  templateId: string,
  slideIndexes: readonly number[],
  outcome: InsertOutcomeHandlers,
): Promise<void> {
  try {
    if (isDeckReadOnly()) throw new DeckReadOnlyError();
    const templates = await currentBrandTemplates(http, cache);
    const template = templates.find((candidate) => candidate.id === templateId);
    const layouts = new Set((template?.slides ?? []).map((slide) => slide.index));
    if (!template || slideIndexes.some((index) => !layouts.has(index))) throw new SelectionUnavailableError();

    const inserted = await insertBrandTemplateLayouts(template, slideIndexes);
    outcome.onAdded(brandLayoutsAddedMessage(inserted));
    session.close();
  } catch (error) {
    // A 404 on the source .pptx means the template was removed.
    const sourceMissing = error instanceof BlobFetchHttpError && error.status === 404;
    const kind = sourceMissing ? "removed" : classifyInsertFailure(error);
    const message = getErrorMessage(error, FALLBACK_BRAND_INSERT_ERROR);
    logSavedBrandTemplatesError("brand-templates-dialog-insert", message);
    if (kind) {
      outcome.onNotice(kind);
      session.close();
      return;
    }
    session.send({ type: "brand-insert-error", message });
  }
}

/** Serves one "switch-brand-template": look the pick up in the last fetch,
 *  park it for ApplyBrandPanel, navigate the pane there, and close this
 *  window. A pick no longer in the cache (stale) is a logged no-op — the
 *  window just stays open on its grid. */
function serveSwitchBrandTemplate(
  session: AssetLibrarySession,
  cache: { current: readonly SavedBrandTemplate[] },
  id: string,
): void {
  const template = cache.current.find((candidate) => candidate.id === id);
  if (!template) {
    logSavedBrandTemplatesError("brand-templates-dialog-switch-stale", `No cached org template for id "${id}"`);
    return;
  }
  setPendingSavedTemplateSelection({ kind: "org", template });
  requestDeckorateView("apply-brand");
  session.close();
}

/**
 * Serves one "presentation-templates-ready"/"presentation-templates-refresh":
 * same shape as serveManifest above, for the Presentation Templates tab. The
 * pane's own background fetch (for servePresentationInsert) races the SAS
 * reply the same way; an early insert just falls back to "no insertable
 * content" there.
 */
async function servePresentationManifest(
  http: HttpClient,
  session: AssetLibrarySession,
  cache: { current: readonly PresentationTemplate[] },
  sasInfoRef: { current: DirectorySasInfo | null },
): Promise<void> {
  let sasInfo: DirectorySasInfo;
  try {
    sasInfo = await requestDirectorySasInfo(http);
  } catch (error) {
    const message = isAuthFailure(error)
      ? SESSION_STILL_STARTING_MESSAGE
      : getErrorMessage(error, FALLBACK_PRESENTATION_SAS_ERROR);
    logPresentationTemplatesError("presentation-templates-dialog-sas", message);
    // Stay open — the dialog has its own error state + Retry, a better
    // recovery path than closing and leaving only a pane toast.
    session.send({ type: "presentation-sas-error", message });
    return;
  }

  sasInfoRef.current = sasInfo;
  fetchPresentationTemplatesFromSasInfo(sasInfo)
    .then((presentations) => {
      cache.current = presentations;
    })
    .catch((error: unknown) => {
      logPresentationTemplatesError(
        "presentation-templates-dialog-pane-cache",
        getErrorMessage(error, FALLBACK_PRESENTATION_SAS_ERROR),
      );
    });

  session.send({ type: "presentation-sas-ready", sasUrl: sasInfo.sasUrl, folderPath: sasInfo.folderPath });
}

/**
 * The presentation manifest as it stands right now. The copy fetched when the
 * window opened can be stale (an administrator may have removed a template
 * since), so an insert re-reads it; if that read fails the earlier copy is
 * used, and only an empty one — nothing to check against — rethrows.
 */
async function currentPresentations(
  cache: { current: readonly PresentationTemplate[] },
  sasInfoRef: { current: DirectorySasInfo | null },
): Promise<{ presentations: readonly PresentationTemplate[]; verified: boolean }> {
  if (!sasInfoRef.current) return { presentations: cache.current, verified: cache.current.length > 0 };
  try {
    cache.current = await fetchPresentationTemplatesFromSasInfo(sasInfoRef.current);
  } catch (error) {
    logPresentationTemplatesError(
      "presentation-templates-dialog-manifest-recheck",
      getErrorMessage(error, FALLBACK_PRESENTATION_SAS_ERROR),
    );
    if (cache.current.length === 0) throw error;
  }
  return { presentations: cache.current, verified: true };
}

/**
 * Serves one "presentation-templates-insert". Never throws, for the same
 * reason as serveInsert.
 *
 * Success and the three BRD failures (read-only deck, lost connection, a
 * template an administrator removed) are all reported in the pane and close
 * the window, because there is nothing left for the user to do inside it. On
 * success the slides have landed after the user's current slide, so the pane
 * confirms, naming the source presentation (BRD 8.9). Any other failure keeps
 * the window open with its own inline message.
 */
async function servePresentationInsert(
  session: AssetLibrarySession,
  cache: { current: readonly PresentationTemplate[] },
  sasInfoRef: { current: DirectorySasInfo | null },
  ids: readonly string[],
  outcome: { readonly onAdded: (message: string) => void; readonly onNotice: (kind: InsertFailureKind) => void },
): Promise<void> {
  try {
    if (isPresentationDeckReadOnly()) throw new PresentationDeckReadOnlyError();
    const { presentations, verified } = await currentPresentations(cache, sasInfoRef);
    const available = new Set(presentations.flatMap((presentation) => presentation.slides.map((slide) => slide.id)));
    if (verified && ids.some((id) => !available.has(id))) throw new PresentationSelectionUnavailableError();

    const inserted = await insertPresentationTemplates(
      presentations.flatMap((presentation) => presentation.slides),
      ids,
    );
    outcome.onAdded(presentationSlidesAddedMessage(inserted, sourcePresentationNames(presentations, ids)));
    session.close();
  } catch (error) {
    const kind = classifyPresentationInsertFailure(error);
    const message = getErrorMessage(error, FALLBACK_PRESENTATION_INSERT_ERROR);
    logPresentationTemplatesError("presentation-templates-dialog-insert", message);
    if (kind) {
      outcome.onNotice(kind);
      session.close();
      return;
    }
    session.send({ type: "presentation-insert-error", message });
  }
}

/**
 * @param onNotice Surfaces a short, user-facing message in the pane. Used only
 *   for failures with no window to show them in — i.e. the window refusing to
 *   open. Must be stable (memoised): it feeds `open`'s identity.
 */
export function useAssetLibraryLauncher(onNotice: (message: string) => void): AssetLibraryLauncher {
  const http = useDeckorateHttp();
  const [isInlineOpen, setIsInlineOpen] = useState(false);
  const [inlineTab, setInlineTab] = useState<AssetLibraryTabId | undefined>(undefined);
  const [paneNoticeState, setPaneNoticeState] = useState<PaneNoticeState | null>(null);

  /** The last manifest we fetched, with its base64 payloads intact. */
  const manifestRef = useRef<readonly SlideTemplate[]>([]);
  /** The SAS the last manifest came from, so an insert can re-read it. */
  const sasInfoRef = useRef<SlideTemplatesSasInfo | null>(null);
  /** The last org brand template list we fetched — "switch-brand-template"
   *  looks up the full entry here rather than re-fetching. */
  const brandTemplatesRef = useRef<readonly SavedBrandTemplate[]>([]);
  /** The last presentation-templates manifest we fetched, with its
   *  sourceFileUrl/slideIndex intact. */
  const presentationTemplatesRef = useRef<readonly PresentationTemplate[]>([]);
  /** The SAS the last presentation manifest came from, so an insert can re-read it. */
  const presentationSasInfoRef = useRef<DirectorySasInfo | null>(null);
  /** Live session, so a second open() can't stack a second window. */
  const sessionRef = useRef<AssetLibrarySession | null>(null);
  /** Set between open() and the window appearing, to close the same gap. */
  const isOpeningRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Pane can unmount (navigation, sign-out) with the window still up —
      // close it rather than leaving an orphan, and clear staged data.
      sessionRef.current?.close();
      sessionRef.current = null;
      clearBrandTemplatesHandoff();
      clearPendingBrandTemplatePreview();
    };
  }, []);

  const handleMessage = useCallback(
    (message: DialogToPaneMessage, session: AssetLibrarySession) => {
      const served = (async () => {
        switch (message.type) {
          case "ready":
          case "refresh":
            await serveManifest(http, session, manifestRef, sasInfoRef);
            return;
          case "insert":
            await serveInsert(session, manifestRef, sasInfoRef, message.ids, {
              onAdded: (text) => {
                setPaneNoticeState(null);
                onNotice(text);
              },
              onNotice: (kind) => setPaneNoticeState({ kind, source: "slide-templates" }),
            });
            return;
          case "brand-templates-insert":
            await serveBrandInsert(http, session, brandTemplatesRef, message.templateId, message.slideIndexes, {
              onAdded: (text) => {
                setPaneNoticeState(null);
                onNotice(text);
              },
              onNotice: (kind) => setPaneNoticeState({ kind, source: "brand-templates" }),
            });
            return;
          case "brand-templates-request":
          case "brand-templates-refresh":
            await serveBrandTemplates(http, session, brandTemplatesRef);
            return;
          case "switch-brand-template":
            serveSwitchBrandTemplate(session, brandTemplatesRef, message.id);
            return;
          case "brand-template-unavailable":
            notifyPreviewedTemplateUnavailable(message.id);
            return;
          case "presentation-templates-ready":
          case "presentation-templates-refresh":
            await servePresentationManifest(http, session, presentationTemplatesRef, presentationSasInfoRef);
            return;
          case "presentation-templates-insert":
            await servePresentationInsert(session, presentationTemplatesRef, presentationSasInfoRef, message.ids, {
              onAdded: (text) => {
                setPaneNoticeState(null);
                onNotice(text);
              },
              onNotice: (kind) => setPaneNoticeState({ kind, source: "presentation-templates" }),
            });
            return;
          case "close":
            session.close();
        }
      })();
      // serveManifest/serveInsert don't throw, but an unexpected rejection
      // must not become an unhandled promise — the window would just sit on
      // its spinner, so trace it.
      served.catch((error: unknown) =>
        logSlideTemplatesError(
          "slide-templates-dialog-serve-unhandled",
          getErrorMessage(error, `Serving "${message.type}" failed`),
        ),
      );
    },
    [http, onNotice],
  );

  const open = useCallback((requestedTab?: unknown) => {
    if (sessionRef.current || isOpeningRef.current) return;
    const initialTab = isAssetLibraryTabId(requestedTab) ? requestedTab : undefined;

    if (!canUseDialog()) {
      // No dialog window to put the Asset Library in (browser dev, tests).
      logEvent("connector.action.invoke", "info", {
        connectorSystemName: DECKORATE_CONNECTOR,
        reason: "asset-library-open-inline",
      });
      setInlineTab(initialTab);
      setIsInlineOpen(true);
      return;
    }

    isOpeningRef.current = true;
    openAssetLibraryDialog({
      onMessage: handleMessage,
      onClosed: () => {
        sessionRef.current = null;
        manifestRef.current = [];
        sasInfoRef.current = null;
        brandTemplatesRef.current = [];
        presentationTemplatesRef.current = [];
        presentationSasInfoRef.current = null;
        // A staged Brand Templates manifest outliving its window would be
        // shown by a later, unrelated open — Slide/Presentation Templates
        // no longer stage anything (they fetch directly), nothing to clear.
        clearBrandTemplatesHandoff();
        clearPendingBrandTemplatePreview();
        logEvent("connector.action.invoke", "info", {
          connectorSystemName: DECKORATE_CONNECTOR,
          reason: "asset-library-dialog-closed",
        });
      },
    }, initialTab)
      .then((session) => {
        // The pane unmounted while the window was opening — don't leave it up.
        if (!isMountedRef.current) {
          session.close();
          return;
        }
        sessionRef.current = session;
      })
      .catch((error: unknown) => {
        // The window itself couldn't open. There's no dialog to show this in,
        // so the pane's toast is the only place left.
        const message = getErrorMessage(error, "Couldn't open the Asset Library window. Please try again.");
        logSlideTemplatesError("slide-templates-dialog-open", message);
        onNotice(message);
      })
      .finally(() => {
        isOpeningRef.current = false;
      });
  }, [handleMessage, onNotice]);

  const closeInline = useCallback(() => {
    logEvent("connector.action.invoke", "info", {
      connectorSystemName: DECKORATE_CONNECTOR,
      reason: "asset-library-inline-closed",
    });
    setIsInlineOpen(false);
  }, []);

  const dismissPaneNotice = useCallback(() => setPaneNoticeState(null), []);

  return {
    open,
    isInlineOpen,
    inlineTab,
    closeInline,
    paneNotice: paneNoticeState?.kind ?? null,
    paneNoticeSource: paneNoticeState?.source ?? null,
    dismissPaneNotice,
  };
}
